import type { NotificationDto } from '@/core/data/notification/notification.interface';
import {
  getDefaultEmailPreferences,
  getDefaultPushPreferences,
  type NotificationPreferences,
  SmsCategory,
} from '@/core/data/user/user.interface';
import { getPushNotificationToken } from '@/core/domain/notification/pushNotifications';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { database } from '@/core/network/firebase';
import { NotificationTopics } from '@/views/composables/constants/gam.constants';
import { add, isBefore } from 'date-fns';
import { ref as firebaseRef, onChildAdded } from 'firebase/database';
import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from './auth.store';

const FETCH_INTERVAL_DURATION = 20 * 1000; // Default interval for refetching notifications (20s)
const DURATION_FUZZ_AMOUNT = 2 * 1000; // The amount of fuzz, to spread requests out in time (20s-22s)
const DURATION_INACTIVITY_MULTIPLIER = 8; // Mow many times slower the requests are made for inactive users (4m 40s)
const INACTIVITY_MINUTES_THRESHOLD = 2; // Time after which intervals slow down
const ACTIVITY_RESTART_THROTTLE_DELAY = 15 * 1000; // 15 sec wait after navigation

function insertNotificationSorted(list: NotificationDto[], item: NotificationDto): NotificationDto[] {
  const itemTime = new Date(item.createdAt).getTime();
  const index = list.findIndex((n) => new Date(n.createdAt).getTime() < itemTime);
  if (index === -1) {
    return [...list, item];
  }
  return [...list.slice(0, index), item, ...list.slice(index)];
}

const logger = createLogger('NotificationStore');

type UnsubscribeNotifications = () => void;

let lastRouteChange: Date = new Date();
let refreshTimeout: number;
let resetTimeout: number;
let unsubscribe: UnsubscribeNotifications | null = null;
let isPolling = false;

export const useNotificationStore = defineStore('notifications', () => {
  const isLoading = ref<boolean>(true);
  const isSubmitting = ref<boolean>(false);
  const isError = ref<boolean>(false);
  const notifications = ref<NotificationDto[]>([]);
  const notification = ref<NotificationDto>();
  const afterCursor = ref<string | null>(null);
  const beforeCursor = ref<string | null>(null);
  const unreadCount = ref<number>(0);

  const router = useRouter();
  const authStore = useAuthStore();
  const { user, firebaseUser } = storeToRefs(authStore);

  const fetchAll = async (): Promise<void> => {
    isError.value = false;
    isLoading.value = true;

    const result = await gambits.notificationService.getNotifications();

    if (Array.isArray(result?.data)) {
      notifications.value = result.data.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setCursor(result?.cursor?.beforeCursor, result?.cursor?.afterCursor);
      // @ts-ignore
      if (typeof result?.unreadCount === 'number') {
        // @ts-ignore
        unreadCount.value = result?.unreadCount;
      }
    }

    if (!result?.data || result.error) {
      isError.value = true;
    }

    isLoading.value = false;
  };

  const clear = async () => {
    isLoading.value = true;
    notifications.value = [];
    setCursor(null, null);
    isLoading.value = false;
    unreadCount.value = 0;
  };

  const fetchMore = async (): Promise<void> => {
    isError.value = false;
    isLoading.value = true;

    const result = await gambits.notificationService.getNotifications({ afterCursor: afterCursor.value });

    if (Array.isArray(result?.data)) {
      notifications.value = notifications.value.concat(result.data);
      setCursor(result?.cursor?.beforeCursor, result?.cursor?.afterCursor);
    }

    if (!result?.data || result.error) {
      isError.value = true;
    }

    isLoading.value = false;
  };

  const setCursor = (newBeforeCursor?: string | null, newAfterCursor?: string | null): void => {
    if (newBeforeCursor === null) {
      beforeCursor.value = null;
    } else if (newBeforeCursor) {
      beforeCursor.value = newBeforeCursor;
    }
    if (newAfterCursor === null) {
      afterCursor.value = null;
    } else if (newAfterCursor) {
      afterCursor.value = newAfterCursor;
    }
  };

  const markAsRead = async (id: string) => {
    let notification = notifications.value.find((n) => n.id === id);
    if (!notification) return;

    isError.value = false;
    isSubmitting.value = true;

    const result = await gambits.notificationService.markAsRead(id);

    if (!result?.data || result.error) {
      isError.value = true;
      isSubmitting.value = false;

      return;
    }

    notification = { ...notification, read: true };
    notifications.value = notifications.value.map((n) => (n.id === id ? notification : n));
    unreadCount.value = result.data.unreadCount;

    isSubmitting.value = false;
  };

  const markAllAsRead = async () => {
    isError.value = false;
    isSubmitting.value = true;

    const result = await gambits.notificationService.markAllAsRead();

    if (!result?.data || result.error) {
      isError.value = true;
      isSubmitting.value = false;

      return;
    }

    unreadCount.value = result.data.unreadCount;

    notifications.value.forEach((notification) => {
      notification.read = true;
    });

    isSubmitting.value = false;
  };

  const deleteAll = async () => {
    isError.value = false;
    isSubmitting.value = true;

    const result = await gambits.notificationService.deleteAll();

    if (!result?.data || result.error) {
      isError.value = true;
      isSubmitting.value = false;
      return;
    }

    notifications.value = [];
    unreadCount.value = 0;
    setCursor(null, null);

    isSubmitting.value = false;
  };

  async function enablePushNotifications(): Promise<boolean> {
    isSubmitting.value = true;
    isError.value = false;

    try {
      const token = await getPushNotificationToken();

      if (typeof token !== 'string' || token.length === 0) {
        return false;
      }

      // Token first: push preferences without a stored token would hide this
      // button while delivering nothing.
      const result = await gambits.userService.storeNotificationToken(token, [NotificationTopics.GAMBIT]);

      if (result.error || result.data !== true) {
        isError.value = true;
        return false;
      }

      const existing = authStore.userDetails?.notificationPreferences;
      const notificationPreferences: NotificationPreferences = {
        email: existing?.email ? { ...getDefaultEmailPreferences(), ...existing.email } : getDefaultEmailPreferences(),
        sms: existing?.sms ?? { [SmsCategory.SPARKS]: false },
        push: existing?.push ?? getDefaultPushPreferences(),
      };

      const saved = await authStore.updateNotificationPreferences(notificationPreferences);
      if (!saved) {
        isError.value = true;
        return false;
      }

      return true;
    } finally {
      isSubmitting.value = false;
    }
  }

  function handleNotificationRefresh() {
    clearTimeout(refreshTimeout);

    const minuteSinceLastActivity = add(lastRouteChange, { minutes: INACTIVITY_MINUTES_THRESHOLD });
    const multiplier = isBefore(minuteSinceLastActivity, new Date()) ? DURATION_INACTIVITY_MULTIPLIER : 1;
    const delay = FETCH_INTERVAL_DURATION * multiplier + Math.random() * DURATION_FUZZ_AMOUNT;
    refreshTimeout = setTimeout(handleNotificationRefresh, delay) as any as number;

    if (
      !useAuthStore().isAuthenticated() ||
      window.location.hostname === 'localhost' ||
      document.visibilityState === 'hidden'
    ) {
      return;
    }

    fetchAll();
  }

  function initializePolling() {
    if (isPolling) return;
    isPolling = true;

    logger.info('Started polling for notifications.');
    router.afterEach(() => {
      clearTimeout(resetTimeout);

      resetTimeout = setTimeout(() => {
        lastRouteChange = new Date();
        handleNotificationRefresh();
      }, ACTIVITY_RESTART_THROTTLE_DELAY) as any as number;
    });

    handleNotificationRefresh();
  }

  function unsubscribeFromBus() {
    if (unsubscribe !== null) {
      unsubscribe();
      unsubscribe = null;
    }
  }

  function subscribeToBus(userId: string) {
    unsubscribeFromBus();

    const busRef = firebaseRef(database, `bus/${userId}`);

    unsubscribe = onChildAdded(
      busRef,
      (snapshot) => {
        const message = snapshot.val();

        if (notifications.value.some((n) => n.id === message.id) === false) {
          const item = { ...(message as NotificationDto) };
          notifications.value = insertNotificationSorted(notifications.value, item);
          unreadCount.value += 1;
        }
      },
      (error) => {
        logger.error(`Realtime news subscription cancelled: ${error.message}`);
        unsubscribe = null;
        initializePolling();
      },
    );
  }

  // Reading the bus requires Firebase auth and a denied listener is cancelled for good,
  // so wait until Firebase is signed in as the current user before subscribing.
  function syncBusSubscription() {
    const userId = user.value?.id;

    if (!userId || firebaseUser.value?.uid !== userId) {
      unsubscribeFromBus();
      return;
    }

    subscribeToBus(userId);
  }

  async function initialize() {
    if (!useAuthStore().isAuthenticated()) {
      logger.info('Not authenticated, skipping notification subscriptions.');
      return;
    }

    await fetchAll();

    syncBusSubscription();
  }

  watch(
    () => user.value?.id,
    (userId) => {
      if (!userId) {
        return;
      }

      initialize();
    },
    { immediate: true },
  );

  watch(() => firebaseUser.value?.uid, syncBusSubscription);

  return {
    notifications,
    notification,
    isLoading,
    isSubmitting,
    isError,
    beforeCursor,
    afterCursor,
    unreadCount,
    setCursor,
    fetchAll,
    fetchMore,
    clear,
    markAsRead,
    markAllAsRead,
    deleteAll,
    enablePushNotifications,
    initialize,
  };
});
